import { promiseWithTimeout } from "./promiseWithTimeout";

const LOADED_KEY_ATTR = "data-google-maps-key";
const SCRIPT_LOAD_TIMEOUT_MS = 12_000;

export type GooglePlaceInstance = {
  fetchFields: (options: { fields: string[] }) => Promise<void>;
  location?:
    | { lat: number; lng: number }
    | { lat: () => number; lng: () => number };
  formattedAddress?: string;
};

export function readGooglePlaceLocation(
  place: GooglePlaceInstance,
): { lat: number; lng: number } | null {
  const location = place.location;
  if (!location) {
    return null;
  }

  const lat =
    typeof location.lat === "function" ? location.lat() : location.lat;
  const lng =
    typeof location.lng === "function" ? location.lng() : location.lng;

  if (typeof lat === "number" && typeof lng === "number") {
    return { lat, lng };
  }

  return null;
}

export type GooglePlacePrediction = {
  placeId: string;
  text?: { text: string };
  mainText?: { text: string };
  secondaryText?: { text: string };
  distanceMeters?: number;
  toPlace: () => GooglePlaceInstance;
};

export type GoogleAutocompleteSuggestion = {
  placePrediction?: GooglePlacePrediction;
};

export type GooglePlacesLibrary = {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (request: {
      input: string;
      sessionToken?: object;
      origin?: { lat: number; lng: number };
      includedPrimaryTypes?: string[];
    }) => Promise<{ suggestions: GoogleAutocompleteSuggestion[] }>;
  };
};

export type GoogleMapInstance = {
  setCenter: (center: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  fitBounds: (bounds: unknown, padding?: number) => void;
};

export type GoogleCircleInstance = {
  setCenter: (center: { lat: number; lng: number }) => void;
  setRadius: (radius: number) => void;
  setOptions: (options: { fillColor?: string; strokeColor?: string }) => void;
  getBounds: () => unknown;
  setMap: (map: GoogleMapInstance | null) => void;
};

export type GoogleMapsCoreLibrary = {
  Map: new (
    element: HTMLElement,
    options: {
      center: { lat: number; lng: number };
      zoom: number;
      disableDefaultUI?: boolean;
      zoomControl?: boolean;
      mapTypeControl?: boolean;
      streetViewControl?: boolean;
      fullscreenControl?: boolean;
    },
  ) => GoogleMapInstance;
  Circle: new (options: {
    map: GoogleMapInstance;
    center: { lat: number; lng: number };
    radius: number;
    strokeColor?: string;
    fillColor?: string;
    fillOpacity?: number;
    strokeOpacity?: number;
    strokeWeight?: number;
    clickable?: boolean;
  }) => GoogleCircleInstance;
};

type GoogleMapsWithImportLibrary = {
  maps: {
    importLibrary: (
      name: string,
    ) => Promise<GooglePlacesLibrary | GoogleMapsCoreLibrary>;
  };
};

function googleMapsWindow(): GoogleMapsWithImportLibrary | undefined {
  return window.google as GoogleMapsWithImportLibrary | undefined;
}

let loadPromise: Promise<GooglePlacesLibrary> | null = null;
let loadedApiKey: string | null = null;

function waitForImportLibrary(): Promise<void> {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const tick = () => {
      if (googleMapsWindow()?.maps?.importLibrary) {
        resolve();
        return;
      }
      if (attempts >= 240) {
        reject(new Error("Google Maps importLibrary did not load"));
        return;
      }
      attempts += 1;
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

function appendMapsScript(apiKey: string): Promise<void> {
  return promiseWithTimeout(
    new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&region=CA`;
      script.async = true;
      script.defer = true;
      script.setAttribute(LOADED_KEY_ATTR, apiKey);

      script.onload = () => {
        waitForImportLibrary().then(resolve).catch(reject);
      };
      script.onerror = () => {
        reject(new Error("Failed to load Google Maps JavaScript API"));
      };

      document.head.appendChild(script);
    }),
    SCRIPT_LOAD_TIMEOUT_MS,
    "Google Maps Places load timed out",
  );
}

export function loadGoogleMapsPlaces(
  apiKey: string,
): Promise<GooglePlacesLibrary> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps Places requires a browser"));
  }

  if (loadPromise && loadedApiKey === apiKey) {
    return loadPromise;
  }

  const existing = document.querySelector<HTMLScriptElement>(
    `script[${LOADED_KEY_ATTR}]`,
  );

  loadedApiKey = apiKey;
  loadPromise = (async () => {
    if (!googleMapsWindow()?.maps?.importLibrary) {
      if (existing?.getAttribute(LOADED_KEY_ATTR) !== apiKey) {
        await appendMapsScript(apiKey);
      } else {
        await promiseWithTimeout(
          waitForImportLibrary(),
          SCRIPT_LOAD_TIMEOUT_MS,
          "Google Maps Places load timed out",
        );
      }
    }

    return googleMapsWindow()!.maps.importLibrary(
      "places",
    ) as Promise<GooglePlacesLibrary>;
  })().catch((error) => {
    loadPromise = null;
    loadedApiKey = null;
    throw error;
  });

  return loadPromise;
}

export async function loadGoogleMapsCore(
  apiKey: string,
): Promise<GoogleMapsCoreLibrary> {
  await loadGoogleMapsPlaces(apiKey);
  const maps = googleMapsWindow()?.maps;
  if (!maps?.importLibrary) {
    throw new Error("Google Maps JavaScript API is not available");
  }
  return maps.importLibrary("maps") as Promise<GoogleMapsCoreLibrary>;
}
